import { useCallback, useEffect, useState } from 'react'
import { readWelcomeSeen, shouldShowWelcome, writeWelcomeSeen } from './welcomeRules'

/**
 * Whether Browse shows the welcome for this scope (an account id, or the guest's). The flag is read once per scope and nothing shows until it
 * has been read, so the page never flashes; a note observed once the inventory is ready ends it for good (and writes the flag), whichever
 * route it came by (there is no Skip). `pending` is true until the flag has been read, so Browse can show nothing in the empty slot
 * for that moment instead of the quiet empty state.
 */
export function useWelcome({ scope, inventoryReady, noteCount, restoring }: { scope: string; inventoryReady: boolean; noteCount: number; restoring: boolean }) {
  const [seen, setSeen] = useState<{ scope: string; value: boolean } | null>(null)
  useEffect(() => {
    let live = true
    void readWelcomeSeen(scope).then(value => { if (live) setSeen({ scope, value }) }, () => { if (live) setSeen({ scope, value: true }) })
    return () => { live = false }
  }, [scope])
  const known = seen?.scope === scope
  const done = !known || seen.value
  const markSeen = useCallback(() => { setSeen({ scope, value: true }); void writeWelcomeSeen(scope).catch(() => undefined) }, [scope])
  useEffect(() => {
    if (known && !seen.value && inventoryReady && noteCount > 0) markSeen()
  }, [known, seen, inventoryReady, noteCount, markSeen])
  return { show: known && shouldShowWelcome({ inventoryReady, noteCount, seen: done, restoring }), pending: !known }
}
